import workspace from "_src/workspace";
import appState from "_src/app_state";

import selector from "_src/manipulators/libs/selector";
import eraser from "_src/manipulators/libs/eraser";
import smoother from "_src/manipulators/libs/smoother";

import output from "_src/engine/output";
import events from "_src/engine/events";

export const eraserTool = {

  selectBySphere(object, position, radius) {

    if (!workspace.checkIsReadyToSelect(object)) {
      output.error(output.DEVELOPERS_ERROR, "Object is not ready to select: " + workspace.getVerbosityAboutObject(object));
      return false;
    }

    const numSelected = selector.selectSphere(object, position, radius);

    if (numSelected === 0) {
      return false;
    }

    events.emit("processing_report", {"objectID": object.id, "selectedVertices": numSelected});

    this.applySelection(object);

    return true;

  },

  selectByMask(object, camera, selectionMask, selectionBoundings, oneSideSelection) {

    if (!workspace.checkIsReadyToSelect(object)) {
      output.error(output.DEVELOPERS_ERROR, "Object is not ready to select: " + workspace.getVerbosityAboutObject(object));
      return false;
    }

    const numSelected = selector.selectByMask(object, camera, selectionMask, null, selectionBoundings, oneSideSelection);

    if (numSelected === 0) {
      return false;
    }

    events.emit("processing_report", {"objectID": object.id, "selectedVertices": numSelected});

    return true;

  },

  selectByPlane(object, center, normal) {

    if (!workspace.checkIsReadyToSelect(object)) {
      output.error(output.DEVELOPERS_ERROR, "Object is not ready to select: " + workspace.getVerbosityAboutObject(object));
      return false;
    }

    const numSelected = selector.selectByPlane(object, normal, center);

    if (numSelected === 0) {
      return false;
    }

    events.emit("processing_report", {"objectID": object.id, "selectedVertices": numSelected});

    return true;

  },

  getMaxBoundings(selectionMask, width, height) {
    return selector.getMaxBoundings(selectionMask, width, height);
  },

  unselectInvisibleVertices(objects, camera) {
    selector.unselectInvisibleVertices(objects, camera);
  },

  erase(object) {

    if (!workspace.checkIsReady(object)) {
      output.error(output.DEVELOPERS_ERROR, "Object is not ready to select: " + workspace.getVerbosityAboutObject(object));
      return false;
    }

    markChunksForSave(object, chunk => chunk.geometry.checkHasSelectedVertices());

    const verticesRemoved = eraser.removeSelectedVertices(object);

    if (verticesRemoved) {

      object.setState(appState.GS_MINMAX, true);

      workspace.applyState(object);

      removeEmptyChunks(object);

    }

    return verticesRemoved;

  },

  applySelection(object) {

    const isSelection = appState.getStateValue("selectionMode") === appState.SM_SELECT;

    const frames = object.frames;

    for (let frame of frames) {

      const selectedChunks = [];
      const selectedChunksIDs = [];

      const model = frame.model;

      const chunks = model.chunks;

      for (let chunk of chunks) {

        const geometry = chunk.geometry;

        if (!geometry.checkHasMarkedVertices()) {
          continue;
        }

        selectedChunks.push(chunk);
        selectedChunksIDs.push(chunk.getID());

      }

      if (selectedChunksIDs.length > 0) {
        for (let selectedChunk of selectedChunks) {
          selector.applySelection(selectedChunk, isSelection);
        }
      }

    }

  },

  dropSelection(object) {
    selector.dropSelection(object);
  }

};

export const smootherTool = {

  smooth(objects, position, radius, strength) {
    let somethingSmoothed = false;

    for (let object of objects) {
      const selectedChunks = [];

      if (!workspace.checkIsReadyToSmooth(object)) {
        output.error(output.DEVELOPERS_ERROR, "Object is not ready to smooth: " + workspace.getVerbosityAboutObject(object));
        return;
      }

      const numSelected = selector.selectSphere(object, position, radius);

      if (numSelected === 0) {
        continue;
      }

      // NOTE: we assume we deal with fusions here
      const model = object.frames[0].model;
      const chunks = model.chunks;

      for (let chunk of chunks) {

        const geometry = chunk.geometry;

        if (!geometry.checkHasMarkedVertices()) {
          continue;
        }

        workspace.markChunkAsUpdated(chunk);

        object.setState(appState.GS_MINMAX, true);

        selectedChunks.push(chunk);

        somethingSmoothed = true;

      }

      events.emit("processing_report", {"objectID": object.id, "smoothedVertices": numSelected});

      smoother.smooth(object, selectedChunks, strength);
    }

    return somethingSmoothed;

  }

};

export const utils = {
  markChunksForSave
}

function markChunksForSave(object, pred) {
  for (const frame of object.frames) {
    for (const chunk of frame.model.chunks) {
      if (pred(chunk)) {
        workspace.markChunkAsUpdated(chunk);
      }
    }
  }
}

function removeEmptyChunks(object) {
  for (const frame of object.frames) {
    const chunksToDelete = [];
    for (const chunk of frame.model.chunks) {
      if (chunk.geometry.empty()) {
        chunksToDelete.push(chunk);
      }
    }

    for (const chunk of chunksToDelete) {
      frame.deleteChunk(chunk);
    }
  }
}
